import React from 'react';
import PropTypes from 'prop-types';
import { gql, useQuery } from '@apollo/client';
import { useRouter } from 'next/router';
import { useEcommerceContext } from '@blaze-cms/ecommerce-context';
import {
  isSubscriptionProduct,
  isSingleIssueProduct,
  getOfferFromPath,
  filterVariantsByOffer,
  hasOfferVariants,
  parseDarwinProductId,
  getChristmasDate
} from './helpers';
import { SUBSCRIPTION_TYPE_OPTION_LABEL } from './constants';
import ProductVariants from './ProductVariants';

const GET_PRODUCT_VARIANTS_QUERY = gql`
  query ecommerceGetProductVariants($id: String!, $currency: String, $entityIdentifier: String) {
    product: getPublishedProduct(id: $id) {
      id
      productType
      pricePerIssue
      brandIds
      url
      darwinProductId
    }
    productVariants: ecommerceGetProductVariants(
      id: $id
      currency: $currency
      entityIdentifier: $entityIdentifier
    ) {
      variantId
      name
      attributes
      price
      isAvailable
      stockLabel
      minQuantity
      maxQuantity
    }
  }
`;

const GET_SINGLE_ISSUE_COUNT_QUERY = gql`
  query getSingleIssueCount($brandIds: [String]) {
    singleIssueCount: countPublishedProducts(
      where: { productType: "magazine_single_issue", brandIds: $brandIds }
    )
  }
`;

const GET_SUBSCRIPTION_PRODUCT_QUERY = gql`
  query getRelatedSubscription($brandIds: [String]) {
    subscriptionProducts: getPublishedProducts(
      where: { productType: "magazine_subscription", brandIds: $brandIds }
      limit: 1
    ) {
      id
      url
    }
  }
`;

const GET_SUBSCRIPTION_VARIANTS_QUERY = gql`
  query getSubscriptionVariants($id: String!, $currency: String, $entityIdentifier: String) {
    subscriptionVariants: ecommerceGetProductVariants(
      id: $id
      currency: $currency
      entityIdentifier: $entityIdentifier
    ) {
      variantId
      price
      attributes
    }
  }
`;

const GET_FORMATTED_PRICE_PER_ISSUE_QUERY = gql`
  query ecommerceGetFormattedPricePerIssue($id: String!, $currency: String) {
    formattedPricePerIssue: ecommerceGetFormattedPricePerIssue(id: $id, currency: $currency)
  }
`;

// With `after`, getDarwinIssues returns just the first issue selected after that date
const GET_DARWIN_START_ISSUE_QUERY = gql`
  query getDarwinStartIssue($darwinProductId: Int!, $after: String) {
    startIssues: getDarwinIssues(darwinProductId: $darwinProductId, after: $after)
  }
`;

const addPublishedEntityPrefix = entityIdentifier => `published_${entityIdentifier}`;

const ProductVariantsContainer = ({ parent, enableAfterChristmasToggle }) => {
  const { itemEntity: parentEntity, itemId: parentId } = parent;
  const { currency } = useEcommerceContext();
  const { asPath } = useRouter() || {};
  const offer = getOfferFromPath(asPath);

  const {
    data: { productVariants, product } = {},
    error,
    loading
  } = useQuery(GET_PRODUCT_VARIANTS_QUERY, {
    variables: {
      id: parentId,
      entityIdentifier: addPublishedEntityPrefix(parentEntity),
      currency
    },
    skip: !parentId || !parentEntity
  });

  const { data: { singleIssueCount } = {} } = useQuery(GET_SINGLE_ISSUE_COUNT_QUERY, {
    variables: { brandIds: product?.brandIds },
    skip: !product?.brandIds || !isSubscriptionProduct(product)
  });

  const { data: { subscriptionProducts } = {} } = useQuery(GET_SUBSCRIPTION_PRODUCT_QUERY, {
    variables: { brandIds: product?.brandIds },
    skip: !product?.brandIds || !isSingleIssueProduct(product)
  });

  const subscriptionProduct = subscriptionProducts?.[0];

  const { data: { formattedPricePerIssue } = {} } = useQuery(GET_FORMATTED_PRICE_PER_ISSUE_QUERY, {
    variables: { id: parentId, currency },
    skip: !parentId || product?.pricePerIssue == null || !isSubscriptionProduct(product)
  });

  const { data: { subscriptionVariants } = {} } = useQuery(GET_SUBSCRIPTION_VARIANTS_QUERY, {
    variables: {
      id: subscriptionProduct?.id,
      currency,
      entityIdentifier: 'published_product'
    },
    skip: !subscriptionProduct?.id
  });

  // The start date comes from the product's darwinProductId, one lookup for the whole
  // product. Without it (or when the toggle can't be shown) there's no lookup and no switch.
  const darwinProductId =
    enableAfterChristmasToggle && isSubscriptionProduct(product)
      ? parseDarwinProductId(product?.darwinProductId)
      : null;

  const { data: { startIssues } = {} } = useQuery(GET_DARWIN_START_ISSUE_QUERY, {
    variables: { darwinProductId, after: getChristmasDate() },
    skip: !darwinProductId
  });

  const afterChristmasStartDate = (darwinProductId && startIssues?.[0]?.selectionDate) || null;

  if (loading) return null;
  if (error) return null;

  // The query returns `price` as a scalar for the requested currency.
  // The display component expects `prices` as an object keyed by currency code.
  const variants = filterVariantsByOffer(productVariants, offer).map(variant => ({
    ...variant,
    prices: variant.price != null ? { [currency || 'gbp']: variant.price } : null
  }));

  const hasSingleIssues = (singleIssueCount || 0) > 0;

  const subscriptionCta = (() => {
    if (!subscriptionProduct || !subscriptionVariants?.length) return null;
    const printVariants = subscriptionVariants.filter(variant =>
      variant.attributes?.selectedOptions?.some(
        opt => opt.optionLabel === SUBSCRIPTION_TYPE_OPTION_LABEL && opt.valueName === 'Print'
      )
    );
    if (!printVariants.length) return null;
    const cheapest = [...printVariants].sort(
      (a, b) =>
        parseFloat(String(a.price).replace(/[^0-9.]/g, '')) -
        parseFloat(String(b.price).replace(/[^0-9.]/g, ''))
    )[0];
    if (!cheapest?.price) return null;
    return { url: subscriptionProduct.url, fromPrice: cheapest.price };
  })();

  return (
    <ProductVariants
      product={product}
      variants={variants}
      itemId={parentId}
      currency={currency}
      hasSingleIssues={hasSingleIssues}
      subscriptionCta={subscriptionCta}
      formattedPricePerIssue={formattedPricePerIssue}
      offer={hasOfferVariants(productVariants, offer) ? offer : null}
      enableAfterChristmasToggle={enableAfterChristmasToggle}
      afterChristmasStartDate={afterChristmasStartDate}
    />
  );
};

ProductVariantsContainer.propTypes = {
  parent: PropTypes.shape({
    itemId: PropTypes.string,
    itemEntity: PropTypes.string
  }).isRequired,
  enableAfterChristmasToggle: PropTypes.bool
};

ProductVariantsContainer.defaultProps = {
  enableAfterChristmasToggle: false
};

export default ProductVariantsContainer;
